<template>
  <div class="st-radio-button" @click="handleClick">
    <div class="st-radio-button__radio-wrapper">
      <input
        :tabindex="getTabIndex"
        class="st-radio-button__input"
        type="radio"
        :required="required"
        :disabled="disabled"
        :checked="isChecked"
        :name="name"
        :id="id"
        :aria-describedby="ariaDescribedby"
        @change="handleChange"
      />
      <span class="st-radio-button__custom-radio" aria-hidden="true"></span>
    </div>
    <label
      :for="id"
      :class="[
        'st-radio-button__label',
        { 'st-radio-button__label--disabled': disabled },
      ]"
    >
      <slot />
    </label>
  </div>
</template>

<script>
export default {
  name: "st-radio-button",
  model: {
    prop: "value",
    event: "input",
  },
  props: {
    required: {
      type: Boolean,
      default: false,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    tabindex: {
      type: String,
    },
    modelValue: {
      type: [String, Boolean, Number],
      default: "",
    },
    value: {
      default: "",
    },
    id: {
      default: "",
    },
    name: {
      default: "",
    },
    ariaDescribedby: {
      type: String,
    },
  },
  computed: {
    isChecked: {
      get() {
        return this.modelValue === this.value;
      },
      set() {
        this.$emit("update:modelValue", this.value);
      },
    },
    getTabIndex: {
      get() {
        return this.tabindex ? this.tabindex : this.disabled ? "-1" : "0";
      },
    },
  },
  methods: {
    handleChange() {
      this.isChecked = !this.isChecked;
    },
    handleClick(event) {
      if (this.disabled) {
        return;
      }
      const firstInput = event.target
        .closest(".st-radio-button")
        ?.querySelector("input");
      firstInput.focus();
      this.handleChange();
    },
  },
  emits: ["update:modelValue"],
};
</script>

<style lang="scss">
@import "../../scss/main.scss";
.st-radio-button {
  display: flex;
  align-items: center;
  padding: rem-calc(4);

  .st-radio-button__radio-wrapper {
    display: grid;
    grid-template-areas: "wrapper-stack";
  }

  input[type="radio"] {
    opacity: 0;
    grid-area: wrapper-stack;
  }

  .st-radio-button__custom-radio {
    cursor: pointer;
    z-index: 1;
    grid-area: wrapper-stack;
    display: grid;
    grid-template-areas: "radio-stack";
    align-items: center;
    justify-items: center;

    &::before {
      grid-area: radio-stack;
      content: "";
      height: rem-calc(20);
      width: rem-calc(20);
      background-color: var(--background-color-secondary);
      border-radius: 50%;
      border: rem-calc(2) solid var(--text-color-secondary);
      box-sizing: initial;
    }
    &::after {
      grid-area: radio-stack;
      content: "";
      height: rem-calc(14);
      width: rem-calc(14);
      border-radius: 50%;
      box-sizing: initial;
    }
  }

  &:hover .st-radio-button__radio-wrapper > .st-radio-button__custom-radio {
    &::before {
      border-color: var(--border-color-secondary);
    }
  }

  &:hover input[type="radio"]:checked + .st-radio-button__custom-radio {
    &::after {
      background: var(--border-color-secondary);
    }
    &::before {
      border-color: var(--border-color-secondary);
    }
  }

  input[type="radio"]:checked + .st-radio-button__custom-radio {
    &::after {
      background: var(--border-color-primary);
    }
    &::before {
      border-color: var(--border-color-primary);
    }
  }

  input[type="radio"]:focus + .st-radio-button__custom-radio {
    &::before {
      box-shadow: 0 0 0 rem-calc(2) var(--background-color-secondary),
        0 0 0 rem-calc(4) var(--border-color-quaternary);
    }
  }

  input[type="radio"]:disabled + .st-radio-button__custom-radio {
    cursor: default;
    &::before {
      border-color: var(--palette-secondary-400);
    }
    &::after {
      display: none;
    }
  }

  .st-radio-button__label {
    cursor: pointer;
    font-weight: 600;
    color: var(--text-color-primary);
    margin-left: rem-calc(10);

    &--disabled {
      cursor: default;
      color: var(--palette-secondary-400);
    }
  }
}
</style>
